import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {
  adminFetchBtwApplications,
  adminFetchBtwStatuses,
  adminFetchAdmins,
  adminUpdateBtwWorkflowStatus,
} from "../../core/axios/endpoints";
import { useUser } from "../../core/context/user";
import BtwFilterBar, { useBtwFilters } from "../../components/btw-filter-bar";
import BtwBoardCard, { DraggableBoardCard } from "../../components/btw-board-card";
import { getStatusColor } from "../../core/btw-status-colors";
import { saveBtwLastUrl } from "../../core/btw-last-url";

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const Column = ({ status, applications, isOver }) => {
  const { setNodeRef } = useDroppable({
    id: `col:${status._id}`,
    data: { statusId: status._id },
  });
  const c = getStatusColor(status.color);

  return (
    <div
      ref={setNodeRef}
      className={`flex h-full w-full flex-col rounded-xl bg-gray-50/70 transition md:w-72 md:flex-shrink-0 ${
        isOver ? "ring-2 ring-emerald-300" : "ring-1 ring-gray-200/70"
      }`}
    >
      <div className="flex flex-shrink-0 items-center justify-between px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`h-2 w-2 flex-shrink-0 rounded-full ${c.dot}`} />
          <span className="truncate text-sm font-semibold text-gray-900">
            {status.name}
          </span>
          <span className="text-xs font-medium text-gray-400 tabular-nums">
            {applications.length}
          </span>
        </div>
        {status.isTerminal ? (
          <span className="text-[10px] uppercase tracking-wider text-gray-400">
            eind
          </span>
        ) : null}
      </div>

      <div
        className="space-y-2 overflow-y-auto px-2 pb-2"
        style={{ flex: 1, minHeight: 0 }}
      >
        {applications.length === 0 ? (
          <div className="flex h-full min-h-[120px] items-center justify-center text-center text-xs text-gray-400">
            <span>-</span>
          </div>
        ) : (
          applications.map((a) => (
            <DraggableBoardCard key={a._id} application={a} />
          ))
        )}
      </div>
    </div>
  );
};

const BtwBatterijBoardPage = () => {
  const { userData } = useUser();
  const [applications, setApplications] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeId, setActiveId] = useState(null);
  const [overColId, setOverColId] = useState(null);
  const [includeTerminal, setIncludeTerminal] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = useMemo(
    () => ({
      search: searchParams.get("search") ?? "",
      workflowStatusId: searchParams.get("workflowStatusId") ?? "",
      assignedToAdminId: searchParams.get("assignedToAdminId") ?? "",
      mine: searchParams.get("mine") === "1",
      unassigned: searchParams.get("unassigned") === "1",
      qualificationStatus: searchParams.get("qualificationStatus") ?? "",
      status: searchParams.get("status") ?? "paid",
      stuck: searchParams.get("stuck") === "1",
      awaitingPartnerPayment: searchParams.get("awaitingPartnerPayment") === "1",
      toInvoice: searchParams.get("toInvoice") === "1",
      archived: searchParams.get("archived") ?? "",
    }),
    [],
  );

  const { filters, setFilter, resetFilters } = useBtwFilters(initialFilters);
  const debouncedSearch = useDebounced(filters.search, 350);

  // Sync filter-state naar URL - overleeft detail-page navigatie + back-knop.
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.workflowStatusId)
      params.set("workflowStatusId", filters.workflowStatusId);
    if (filters.assignedToAdminId)
      params.set("assignedToAdminId", filters.assignedToAdminId);
    if (filters.mine) params.set("mine", "1");
    if (filters.unassigned) params.set("unassigned", "1");
    if (filters.qualificationStatus)
      params.set("qualificationStatus", filters.qualificationStatus);
    if (filters.status) params.set("status", filters.status);
    if (filters.stuck) params.set("stuck", "1");
    if (filters.awaitingPartnerPayment)
      params.set("awaitingPartnerPayment", "1");
    if (filters.toInvoice) params.set("toInvoice", "1");
    if (filters.archived) params.set("archived", filters.archived);
    setSearchParams(params, { replace: true });
    const qs = params.toString();
    saveBtwLastUrl(
      "/panel/admin/btw-batterij-board",
      qs ? `?${qs}` : "",
    );
  }, [filters, setSearchParams]);

  useEffect(() => {
    Promise.all([adminFetchBtwStatuses(), adminFetchAdmins()])
      .then(([statusRes, adminRes]) => {
        setStatuses(statusRes?.data ?? statusRes ?? []);
        setAdmins(adminRes?.data ?? adminRes ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminFetchBtwApplications({
      page: 1,
      limit: 500,
      search: debouncedSearch,
      assignedToAdminId: filters.assignedToAdminId,
      mine: filters.mine,
      unassigned: filters.unassigned,
      qualificationStatus: filters.qualificationStatus,
      status: filters.status,
      stuck: filters.stuck,
      awaitingPartnerPayment: filters.awaitingPartnerPayment,
      toInvoice: filters.toInvoice,
      archived: filters.archived,
      sort: "activity",
      // intentionally NOT filtering by workflowStatusId - board groups by status
    })
      .then((res) => {
        if (cancelled) return;
        const docs = res?.data?.docs ?? res?.docs ?? [];
        setApplications(docs);
      })
      .catch((err) => console.error("Board load failed", err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    debouncedSearch,
    filters.assignedToAdminId,
    filters.mine,
    filters.unassigned,
    filters.qualificationStatus,
    filters.status,
    filters.stuck,
    filters.awaitingPartnerPayment,
    filters.toInvoice,
    filters.archived,
    reloadKey,
  ]);

  const visibleStatuses = useMemo(
    () =>
      statuses.filter(
        (s) =>
          !s.archived &&
          (includeTerminal || !s.isTerminal) &&
          (!filters.workflowStatusId || s._id === filters.workflowStatusId),
      ),
    [statuses, includeTerminal, filters.workflowStatusId],
  );

  // Mobile: één status-kolom tegelijk tonen via tabs.
  const [activeStatusId, setActiveStatusId] = useState(null);
  useEffect(() => {
    if (visibleStatuses.length === 0) {
      setActiveStatusId(null);
      return;
    }
    if (
      !activeStatusId ||
      !visibleStatuses.find((s) => String(s._id) === String(activeStatusId))
    ) {
      setActiveStatusId(String(visibleStatuses[0]._id));
    }
  }, [visibleStatuses, activeStatusId]);

  const grouped = useMemo(() => {
    const map = new Map();
    visibleStatuses.forEach((s) => map.set(String(s._id), []));
    applications.forEach((a) => {
      const sid = String(a.workflowStatusId?._id ?? a.workflowStatusId ?? "");
      if (map.has(sid)) map.get(sid).push(a);
    });
    return map;
  }, [applications, visibleStatuses]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const activeApp = useMemo(
    () => applications.find((a) => a._id === activeId) ?? null,
    [activeId, applications],
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    setOverColId(over?.data?.current?.statusId ?? null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColId(null);
    if (!over) return;

    const targetStatusId = over.data?.current?.statusId;
    if (!targetStatusId) return;

    const app = applications.find((a) => a._id === active.id);
    if (!app) return;
    const currentStatusId = String(
      app.workflowStatusId?._id ?? app.workflowStatusId ?? "",
    );
    if (currentStatusId === String(targetStatusId)) return;

    // Bewaar de pre-drag snapshot voor rollback bij API-fail.
    const previousApp = app;
    const targetStatus = statuses.find(
      (s) => String(s._id) === String(targetStatusId),
    );
    setApplications((prev) =>
      prev.map((a) =>
        a._id === app._id
          ? {
              ...a,
              workflowStatusId: targetStatus,
              lastActivityAt: new Date().toISOString(),
            }
          : a,
      ),
    );

    try {
      const res = await adminUpdateBtwWorkflowStatus(app._id, targetStatusId);
      const updated = res?.data ?? res;
      if (updated && updated._id) {
        setApplications((prev) =>
          prev.map((a) => (a._id === updated._id ? { ...a, ...updated } : a)),
        );
      }
    } catch (err) {
      console.error("Status update mislukt", err);
      // Rol synchroon terug zodat de kaart niet in de verkeerde kolom blijft hangen
      // tot de refetch (die later toch nog binnenkomt om eventuele andere drift te corrigeren).
      setApplications((prev) =>
        prev.map((a) => (a._id === previousApp._id ? previousApp : a)),
      );
      window.alert(err?.response?.data?.error ?? "Status wijzigen mislukt.");
      setReloadKey((k) => k + 1);
    }
  };

  return (
    <div
      className="page-wrapper px-6 pt-24 pb-6"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            BTW Batterij - Board
          </h2>
          <p className="text-sm text-gray-500">
            Sleep aanvragen tussen kolommen om de workflow-status te wijzigen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm">
            <input
              type="checkbox"
              checked={includeTerminal}
              onChange={(e) => setIncludeTerminal(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-400"
            />
            Toon eindstations
          </label>
          <Link
            to="/panel/admin/btw-batterij-instellingen"
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Statussen beheren
          </Link>
        </div>
      </div>

      <BtwFilterBar
        view="board"
        listTo="/panel/admin/btw-batterij-overzicht"
        boardTo="/panel/admin/btw-batterij-board"
        filters={filters}
        setFilter={setFilter}
        resetFilters={resetFilters}
        statuses={statuses}
        admins={admins}
        currentUserId={userData?._id}
        showStatusFilter={false}
      />

      {loading && applications.length === 0 ? (
        <div className="rounded-xl bg-white p-10 text-center text-gray-500 shadow-sm ring-1 ring-gray-200">
          Board laden…
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveId(null);
            setOverColId(null);
          }}
        >
          {/* Mobile: status-tabs */}
          <div className="mb-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 md:hidden">
            {visibleStatuses.map((s) => {
              const count = (grouped.get(String(s._id)) ?? []).length;
              const isActive = String(s._id) === String(activeStatusId);
              const c = getStatusColor(s.color);
              return (
                <button
                  key={s._id}
                  type="button"
                  onClick={() => setActiveStatusId(String(s._id))}
                  className={`flex-none inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    isActive
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                  {s.name}
                  <span
                    className={`ml-0.5 rounded-full px-1.5 text-[10px] ${
                      isActive ? "bg-white/20" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Desktop: horizontal kolom-board */}
          <div
            className="hidden gap-3 overflow-x-auto pb-2 md:flex"
            style={{ flex: 1, minHeight: 0 }}
          >
            {visibleStatuses.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 p-8 text-sm text-gray-500">
                Geen statussen om weer te geven. Maak ze aan via &laquo;Statussen beheren&raquo;.
              </div>
            ) : (
              visibleStatuses.map((s) => (
                <Column
                  key={s._id}
                  status={s}
                  applications={grouped.get(String(s._id)) ?? []}
                  isOver={overColId === s._id}
                />
              ))
            )}
          </div>

          {/* Mobile: actieve kolom full-width */}
          <div className="block md:hidden" style={{ flex: 1, minHeight: 0 }}>
            {visibleStatuses.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 p-8 text-sm text-gray-500">
                Geen statussen om weer te geven.
              </div>
            ) : (
              (() => {
                const s = visibleStatuses.find(
                  (x) => String(x._id) === String(activeStatusId),
                );
                if (!s) return null;
                return (
                  <Column
                    status={s}
                    applications={grouped.get(String(s._id)) ?? []}
                    isOver={overColId === s._id}
                  />
                );
              })()
            )}
          </div>

          <DragOverlay dropAnimation={{ duration: 150 }}>
            {activeApp ? <BtwBoardCard application={activeApp} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default BtwBatterijBoardPage;
